/**
 * NFC Solutions — self-hosted APK update check.
 *
 * When the user is running the Capacitor APK, this module polls the
 * backend `/api/apk/latest` endpoint on a schedule and surfaces a
 * lightweight, dismissible toast if a newer native build is
 * available.
 *
 * Design notes
 * ────────────
 *   • This module is browser-safe: on the web build all exports
 *     resolve to no-ops (`checkForApkUpdate` returns `null`, the
 *     periodic checker never arms), so there is zero runtime cost
 *     outside the native shell.
 *
 *   • We never install updates silently. The user gets a toast with
 *     "Install" / "Later" and Android's package installer handles
 *     the actual APK swap — which preserves IndexedDB, localStorage,
 *     Preferences, offline auth credentials and the event queue,
 *     because Android's normal upgrade path keeps the app's private
 *     storage intact.
 *
 *   • Semver comparison is version-code based (an integer). That
 *     matches Android's own upgrade semantics and avoids parsing
 *     ambiguity across pre-release strings.
 *
 *   • CapGo Live Updates (JS-only OTA) is layered on top later and
 *     handled by @capgo/capacitor-updater in a sibling module. This
 *     module is only responsible for the *native* APK channel.
 */

import { getPlatform } from "@/lib/platform";

const CHECK_INTERVAL_MS = 6 * 60 * 60 * 1000;     // 6h between polls
const RETRY_BACKOFF_MS  = 30 * 60 * 1000;         // 30m after a network error
const DISMISS_MEMORY_MS = 6 * 60 * 60 * 1000;     // remember "Later" for 6h

/**
 * @typedef {{
 *   version_name: string,
 *   version_code: number,
 *   min_supported_version_code: number,
 *   download_url: string,
 *   sha256: string,
 *   release_notes: string,
 *   released_at: string,
 * }} ApkLatestMetadata
 */

let _timer = null;
let _lastCheckAt = 0;

/**
 * Hit the /api/apk/latest endpoint on the configured backend and
 * return the parsed metadata, or `null` on any failure.
 */
export async function fetchApkLatest() {
  const p = getPlatform();
  const baseUrl = p.backendUrl || "";
  if (!baseUrl) return null;
  try {
    const res = await fetch(`${baseUrl}/api/apk/latest`, {
      method: "GET",
      credentials: "omit",
      cache: "no-store",
    });
    if (!res.ok) return null;
    const json = await res.json();
    if (!json || typeof json !== "object") return null;
    return {
      version_name: String(json.version_name || ""),
      version_code: Number(json.version_code || 0),
      min_supported_version_code: Number(json.min_supported_version_code || 0),
      download_url: String(json.download_url || ""),
      sha256: String(json.sha256 || ""),
      release_notes: String(json.release_notes || ""),
      released_at: String(json.released_at || ""),
    };
  } catch (_) {
    return null;
  }
}

/**
 * Ask Android to open the APK download URL. When the user completes
 * the OS install prompt, Android reopens the app on the new version.
 * IndexedDB, Preferences and other app-scoped data are preserved.
 *
 * On the browser this falls back to `window.location.assign` which
 * simply triggers a normal download.
 */
/**
 * Session 30.38 — One-tap install. Preferred path is the native
 * `NfcsInstaller` plugin: it downloads the APK into the app cache (with
 * progress events + SHA-256 check) and opens Android's package-installer
 * sheet directly — no browser hop, no download notification to hunt for.
 *
 * Returns a string describing which path ran:
 *   "installer"  — native plugin downloaded + launched the install sheet
 *   "settings"   — user must first allow "install unknown apps"; the
 *                  settings page was opened (call again afterwards)
 *   "browser"    — fell back to the system browser / Custom Tab
 *   false        — nothing could be launched
 *
 * `opts.onProgress({ percent, received, total })` fires during the
 * native download. `opts.sha256` enables integrity verification.
 */
function _nativeInstaller() {
  try {
    const cap = typeof window !== "undefined" ? window.Capacitor : null;
    if (!cap || typeof cap.isNativePlatform !== "function" || !cap.isNativePlatform()) return null;
    if (cap.getPlatform && cap.getPlatform() !== "android") return null;
    const p = (cap.Plugins || {}).NfcsInstaller;
    return p && typeof p.downloadAndInstall === "function" ? p : null;
  } catch (_) { return null; }
}

export async function installerNeedsPermission() {
  const p = _nativeInstaller();
  if (!p) return false;
  try {
    const r = await p.canInstall();
    return r && r.allowed === false;
  } catch (_) { return false; }
}

export async function openInstallPermissionSettings() {
  const p = _nativeInstaller();
  if (!p) return false;
  try { await p.openInstallSettings(); return true; } catch (_) { return false; }
}

export async function launchApkInstall(downloadUrl, opts = {}) {
  if (!downloadUrl || typeof downloadUrl !== "string") return false;
  const absolute = (() => {
    try {
      const u = new URL(downloadUrl, window.location.origin);
      // Session 30.36 — the server refuses to hand the raw binary to an
      // Android WebView (it serves a "open in browser" page instead).
      // Chrome / Custom Tabs / the native installer want the real file,
      // so ask for it explicitly.
      if (u.pathname.includes("/api/downloads/") && !u.searchParams.has("direct")) u.searchParams.set("direct", "1");
      return u.toString();
    } catch (_) { return downloadUrl; }
  })();

  // ── Path 1: native one-tap installer ─────────────────────────────
  const native = _nativeInstaller();
  if (native) {
    let handle = null;
    try {
      try {
        const perm = await native.canInstall();
        if (perm && perm.allowed === false) {
          await native.openInstallSettings();
          return "settings";
        }
      } catch (_) { /* older Android — proceed */ }
      if (typeof opts.onProgress === "function" && typeof native.addListener === "function") {
        try { handle = await native.addListener("downloadProgress", (ev) => { try { opts.onProgress(ev || {}); } catch (_) { /* noop */ } }); } catch (_) { handle = null; }
      }
      await native.downloadAndInstall({ url: absolute, sha256: opts.sha256 || "" });
      return "installer";
    } catch (err) {
      // Surface the reason to the caller, then fall through to the browser.
      if (typeof opts.onError === "function") { try { opts.onError(err); } catch (_) { /* noop */ } }
    } finally {
      try { if (handle && typeof handle.remove === "function") handle.remove(); } catch (_) { /* noop */ }
    }
  }

  // ── Path 2: system browser / Chrome Custom Tab ───────────────────
  // Session 30.35 — Hand the APK to the SYSTEM browser (Chrome Custom
  // Tab) via @capacitor/browser. Navigating the Capacitor WebView itself
  // to the download URL rendered the binary as a "page full of code" on
  // some phones (no DownloadListener in the WebView); Chrome downloads
  // the file and shows Android's "Install / Update" sheet.
  try {
    const cap = typeof window !== "undefined" ? window.Capacitor : null;
    const isNative = !!(cap && typeof cap.isNativePlatform === "function" && cap.isNativePlatform());
    if (isNative) {
      const Browser = cap?.Plugins?.Browser;
      if (Browser && typeof Browser.open === "function") {
        await Browser.open({ url: absolute, presentationStyle: "fullscreen" });
        return "browser";
      }
      try {
        const mod = await import("@capacitor/browser");
        await mod.Browser.open({ url: absolute, presentationStyle: "fullscreen" });
        return "browser";
      } catch (_) { /* plugin unavailable — fall through */ }
    }
  } catch (_) { /* fall through */ }
  try {
    if (typeof window !== "undefined") {
      const w = window.open(absolute, "_system");
      if (w) return "browser";
    }
  } catch (_) { /* noop */ }
  try { window.location.assign(absolute); return "browser"; } catch (_) { return false; }
}

/**
 * Version comparison. Returns:
 *   1  if `latest` is newer than `installed`
 *   0  if same
 *  -1  if `installed` is newer (should never happen in practice)
 */
export function compareVersionCodes(installed, latest) {
  const a = Number(installed) || 0;
  const b = Number(latest)    || 0;
  if (b > a) return 1;
  if (b < a) return -1;
  return 0;
}

/**
 * Was the user shown a "Later" toast recently? If so we should not
 * bug them again for a while. Persisted in localStorage under
 * `nfcs.apkUpdateDismissedAt`.
 */
function _wasRecentlyDismissed() {
  try {
    const raw = window.localStorage.getItem("nfcs.apkUpdateDismissedAt");
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return false;
    return (Date.now() - ts) < DISMISS_MEMORY_MS;
  } catch (_) {
    return false;
  }
}

function _rememberDismissed() {
  try { window.localStorage.setItem("nfcs.apkUpdateDismissedAt", String(Date.now())); }
  catch (_) { /* noop */ }
}

/**
 * Immediate check — used on app boot and after connectivity recovery.
 * Returns the update metadata if an update is available, else `null`.
 *
 * Never throws.
 */
export async function checkForApkUpdate() {
  const p = getPlatform();
  if (!p.isNative) return null;
  _lastCheckAt = Date.now();

  const installedCode = Number(
    process.env.REACT_APP_VERSION_CODE
    || _extractCodeFromVersion(p.appVersion)
    || 0,
  );
  const meta = await fetchApkLatest();
  if (!meta) return null;
  if (!meta.download_url) return null;
  // Session 27.92 — Two guards against phantom "install update"
  // prompts:
  //
  // 1. `installedCode === 0` means we couldn't determine THIS build's
  //    version (REACT_APP_VERSION_CODE wasn't stamped at build time
  //    AND `p.appVersion` fell back to "0.0.0"). Under the old
  //    logic, any server response with `version_code > 0` would
  //    then look "newer" and pop the toast — even though we don't
  //    actually know if this device is behind. Silently skip
  //    prompting in this case; there's no honest way to compare.
  //
  // 2. `meta.version_code <= 0` means the backend explicitly reports
  //    "no tagged release published yet" (i.e. the operator has
  //    never set a version on the admin upload, so there's no
  //    canonical build to advertise). We must not prompt because
  //    there's nothing to install.
  if (installedCode <= 0) return null;
  if (!meta.version_code || meta.version_code <= 0) return null;
  if (compareVersionCodes(installedCode, meta.version_code) <= 0) return null;
  return meta;
}

/**
 * Best-effort parsing of `X.Y.Z` into a numeric code — used only as a
 * fallback when REACT_APP_VERSION_CODE isn't explicitly set. Turns
 * "1.4.2" into 1004002 so ordering stays intuitive.
 */
function _extractCodeFromVersion(v) {
  const parts = String(v || "0.0.0").split(".").map((x) => parseInt(x, 10) || 0);
  const [a, b, c] = [parts[0] || 0, parts[1] || 0, parts[2] || 0];
  return a * 1_000_000 + b * 1_000 + c;
}

/**
 * Arm a repeating check every `CHECK_INTERVAL_MS`. Calls
 * `onUpdate(meta)` if an eligible update is found and the user hasn't
 * dismissed it recently.
 *
 * Returns a function that stops the checker — call it from React
 * `useEffect` cleanup so route re-renders don't stack multiple timers.
 */
export function armPeriodicApkUpdateCheck(onUpdate) {
  const p = getPlatform();
  if (!p.isNative) return () => {};
  if (typeof onUpdate !== "function") return () => {};

  const tick = async () => {
    try {
      if (_wasRecentlyDismissed()) return;
      const meta = await checkForApkUpdate();
      if (meta) onUpdate(meta);
    } catch (_) { /* noop */ }
  };

  // Fire once shortly after arm so users see updates during the
  // current session, not only on the next 6h cycle.
  const bootTimeout = window.setTimeout(tick, 8000);

  const armInterval = () => {
    _timer = window.setInterval(tick, CHECK_INTERVAL_MS);
  };
  armInterval();

  // If the app was offline at check time, retry sooner when the OS
  // reports network is back.
  const onOnline = () => {
    if ((Date.now() - _lastCheckAt) > RETRY_BACKOFF_MS) tick();
  };
  window.addEventListener("online", onOnline);

  return () => {
    window.clearTimeout(bootTimeout);
    if (_timer) window.clearInterval(_timer);
    _timer = null;
    window.removeEventListener("online", onOnline);
  };
}

/** Explicitly record that the user chose "Later" on the toast. */
export function recordUserDeferredUpdate() {
  _rememberDismissed();
}
